import { useState, useMemo } from 'react';
import { C, iS, PROGRAMS, PROGRAM_MAP, GAME_DURATIONS, makeCode, todayStr } from './config.js';
import { API } from './api.js';
import { Modal, Field, Btn } from './common.jsx';

export function AddCourseModal({ onClose, onSaved, toast, existingCourses }) {
  const [programId, setProgramId] = useState(PROGRAMS[0].id);
  const [courseName, setCourseName] = useState("");
  const [duration,  setDuration]  = useState(60);
  const [useDate,   setUseDate]   = useState(todayStr());
  const [capacity,  setCapacity]  = useState(20);
  const [saving,    setSaving]    = useState(false);

  const prog = PROGRAM_MAP[programId];

  // 회차 자동 계산: 오늘 날짜 + 프로그램 기준 기존 과정 수 + 1
  const autoRound = useMemo(() => {
    const count = (existingCourses || []).filter(
      c => c.programId === programId && c.useDate === useDate
    ).length;
    return count + 1;
  }, [programId, useDate, existingCourses]);

  const save = async () => {
    if (!prog) return;
    if (!prog.codeTable) { toast("codeTable이 설정되지 않았습니다. PROGRAMS 설정을 확인하세요.", "error"); return; }
    setSaving(true);
    try {
      // 1. 과정 목록에 저장
      await API.course.create({
        courseName: courseName.trim() || `${prog.name} ${autoRound}회차`,
        programId,
        program:  prog.name,
        prefix:   prog.prefix,
        round:    autoRound,
        useDate,
        capacity: Number(capacity),
        duration: Number(duration),
        tableId:  prog.tableId,
      });

      // 2. codeTable에 입장 코드 생성 (참여자 테이블 X)
      const roundAlpha = String.fromCharCode(96 + autoRound); // 1→a, 2→b
      const codeBatch  = Array.from({ length: Number(capacity) }, (_, i) => ({
        entryCode:   makeCode(prog.prefix, autoRound, i + 1),
        useDate,
        round:       roundAlpha,
        usageCount:  0,
      }));
      const result = await API.codes(prog).createBatch(codeBatch);


      toast(`과정 추가 완료. 코드 ${result.created}개 생성${result.skipped > 0 ? ` (중복 ${result.skipped}개 스킵)` : ""}`);
      onSaved();
    } catch (e) {
      toast("저장 실패: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="과정 추가" onClose={onClose}>

      {/* 프로그램 선택 */}
      <Field label="프로그램">
        <select value={programId} onChange={e => setProgramId(e.target.value)} style={iS}>
          {PROGRAMS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>

      {/* 과정명 */}
      <Field label="과정명" hint={`비워두면 "${prog?.name} ${autoRound}회차" 로 자동 설정됩니다`}>
        <input
          value={courseName}
          onChange={e => setCourseName(e.target.value)}
          placeholder={`예) ${prog?.name} ${autoRound}회차`}
          style={iS}
        />
      </Field>

      {/* 사용 일자 */}
      <Field label="사용 일자">
        <input value={useDate} onChange={e => setUseDate(e.target.value)} type="date" style={iS}/>
      </Field>

      {/* 회차 — 자동 (표시만) */}
      <Field label="회차 (자동)">
        <div style={{ ...iS, background:"#f5f5f5", color:C.t2, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontWeight:700, color:C.accentTxt }}>{autoRound}회차</span>
          <span style={{ fontSize:12, color:C.t3 }}>— {useDate} 기준 {prog?.name} {autoRound}번째 과정</span>
        </div>
      </Field>

      {/* 인원 */}
      <Field label="코드 생성 수" hint="입력한 수만큼 입장 코드가 자동 생성됩니다">
        <input value={capacity} onChange={e => setCapacity(e.target.value)} type="number" min={1} max={999} style={iS}/>
      </Field>

      {/* 게임 시간 — 10분 단위 선택 */}
      <Field label="게임 시간">
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          {GAME_DURATIONS.map(d => (
            <button key={d} onClick={() => setDuration(d)}
              style={{
                padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:600,
                cursor:"pointer", border:`1.5px solid ${duration===d ? C.accent : C.border2}`,
                background: duration===d ? C.accentBg : C.surface,
                color: duration===d ? C.accentTxt : C.t2,
                transition:"all .15s",
              }}
            >{d}분</button>
          ))}
        </div>
      </Field>

      {/* 미리보기 */}
      <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px", marginBottom:20 }}>
        <div style={{ fontSize:12, color:C.t3, marginBottom:8, fontWeight:700 }}>생성 미리보기</div>
        <div style={{ fontSize:13, color:C.t1, fontWeight:600, marginBottom:4 }}>
          {prog?.name} {autoRound}회차 · {useDate} · {duration}분
        </div>
        <div style={{ fontSize:12, color:C.t2 }}>
          참여자 코드: <code style={{ fontFamily:"monospace", color:C.accentTxt }}>
            {makeCode(prog?.prefix||"", autoRound, 1)}
          </code> ~ <code style={{ fontFamily:"monospace", color:C.accentTxt }}>
            {makeCode(prog?.prefix||"", autoRound, Number(capacity)||1)}
          </code> ({capacity}개)
        </div>
      </div>

      <div style={{ display:"flex", gap:8 }}>
        <Btn variant="ghost" onClick={onClose}>취소</Btn>
        <Btn onClick={save} disabled={saving} style={{ flex:1, justifyContent:"center" }}>
          {saving ? "생성 중…" : "과정 추가 + 코드 생성"}
        </Btn>
      </div>
    </Modal>
  );
}

/* ================================================================
   EditCourseModal
   ================================================================ */
export function EditCourseModal({ course, onClose, onSaved, toast }) {
  const [useDate,  setUseDate]  = useState(course.useDate   || todayStr());
  const [capacity, setCapacity] = useState(course.capacity  || 20);
  const [duration, setDuration] = useState(course.duration  || 60);
  const [saving,   setSaving]   = useState(false);

  const save = async () => {
    const prog        = course._prog;
    const newCapacity = Number(capacity);
    const oldCapacity = Number(course.capacity) || 0;
    setSaving(true);
    try {
      // 1. 과정 정보 업데이트
      await API.course.update(course.objectId, {
        useDate, capacity: newCapacity, duration: Number(duration),
      });

      // 2. 인원이 늘어난 경우 → 추가된 만큼 코드 생성
      if (prog?.codeTable && newCapacity > oldCapacity) {
        const addCount   = newCapacity - oldCapacity;
        const roundAlpha = String.fromCharCode(96 + Number(course.round));
        const codeBatch  = Array.from({ length: addCount }, (_, i) => ({
          entryCode:  makeCode(prog.prefix, course.round, oldCapacity + i + 1),
          useDate:    course.useDate,
          round:      roundAlpha,
          usageCount: 0,
        }));
        const result = await API.codes(prog).createBatch(codeBatch);
        toast(`수정 완료. 코드 ${result.created}개 추가 생성`);
      } else {
        toast("수정되었습니다.");
      }


      onSaved();
    } catch (e) {
      toast("수정 실패: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="과정 수정" onClose={onClose}>
      <Field label="프로그램">
        <div style={{ ...iS, background:"#f5f5f5", color:C.t3 }}>{course.program || "—"}</div>
      </Field>
      <Field label="사용 일자">
        <input value={useDate} onChange={e => setUseDate(e.target.value)} type="date" style={iS}/>
      </Field>
      <Field label="코드 생성 수">
        <input value={capacity} onChange={e => setCapacity(e.target.value)} type="number" min={1} style={iS}/>
      </Field>
      <Field label="게임 시간">
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          {GAME_DURATIONS.map(d => (
            <button key={d} onClick={() => setDuration(d)}
              style={{
                padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:600,
                cursor:"pointer", border:`1.5px solid ${duration===d ? C.accent : C.border2}`,
                background: duration===d ? C.accentBg : C.surface,
                color: duration===d ? C.accentTxt : C.t2, transition:"all .15s",
              }}
            >{d}분</button>
          ))}
        </div>
      </Field>
      <div style={{ display:"flex", gap:8, marginTop:4 }}>
        <Btn variant="ghost" onClick={onClose}>취소</Btn>
        <Btn onClick={save} disabled={saving} style={{ flex:1, justifyContent:"center" }}>
          {saving ? "저장 중…" : "저장"}
        </Btn>
      </div>
    </Modal>
  );
}

/* ================================================================
   AddParticipantModal (수동 추가 — 코드로 역할 설정)
   ================================================================ */
export function AddParticipantModal({ course, onClose, onSaved, toast }) {
  const prog = course._prog;
  const [userCode, setUserCode] = useState("");
  const [nickname, setNickname] = useState("");
  const [score,    setScore]    = useState("");
  const [time,     setTime]     = useState("");
  const [role,     setRole]     = useState(prog?.roles?.[0] || "");
  const [saving,   setSaving]   = useState(false);

  const save = async () => {
    if (!userCode) { toast("코드를 입력하세요.", "error"); return; }
    if (!prog)     { toast("프로그램 정보 없음.", "error"); return; }
    setSaving(true);
    try {
      const data = { userCode, nickname: nickname||null, score: score?Number(score):null, time: time?Number(time):null };
      if (prog.roles && prog.roles.length > 0) data.userRole = role; // 역할 있는 프로그램만
      await API.ptcp(prog).create(course.tableId, data);
      toast("참여자가 추가되었습니다."); onSaved();
    } catch (e) {
      toast("추가 실패: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`참여자 수동 추가 — ${course.courseName}`} onClose={onClose}>
      <Field label="참여 코드" hint={`예) ${makeCode(prog?.prefix||"", course.round||1, 1)}`}>
        <input value={userCode} onChange={e => setUserCode(e.target.value)} placeholder="코드 입력" style={iS}/>
      </Field>
      <Field label="닉네임 (선택)">
        <input value={nickname} onChange={e => setNickname(e.target.value)} placeholder="—" style={iS}/>
      </Field>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        <Field label="점수 (선택)">
          <input value={score} onChange={e => setScore(e.target.value)} type="number" placeholder="—" style={iS}/>
        </Field>
        <Field label="시간 (초, 선택)">
          <input value={time} onChange={e => setTime(e.target.value)} type="number" placeholder="—" style={iS}/>
        </Field>
      </div>
      {/* 역할: 해당 프로그램에 roles가 있을 때만 표시 */}
      {prog?.roles?.length > 0 && (
        <Field label="역할">
          <select value={role} onChange={e => setRole(e.target.value)} style={iS}>
            {prog.roles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
      )}
      <div style={{ display:"flex", gap:8 }}>
        <Btn variant="ghost" onClick={onClose}>취소</Btn>
        <Btn onClick={save} disabled={saving} style={{ flex:1, justifyContent:"center" }}>
          {saving ? "저장 중…" : "추가"}
        </Btn>
      </div>
    </Modal>
  );
}

/* ================================================================
   DetailPanel
   ================================================================ */